import type { ColumnId, DependencyType, ScheduleHealth } from '../lib/constants'
import { formatDays, formatIsoDate, type ISODate } from '../lib/format'
import { formatPredecessors, type LocalLink } from '../lib/predecessors'
import { visibleRowOf, type Tree, type VisibleRow } from '../lib/tree'
import type { ScheduleActivity, ScheduleDependency } from '../types'

export interface PrintRow { id: string; rowNumber: number; depth: number; activity: ScheduleActivity; predecessorsText: string }
export interface PrintEndpoint { start: ISODate; finish: ISODate; isMilestone: boolean }
export interface PrintLink { id: string; type: DependencyType; fromRow: number; toRow: number; from: PrintEndpoint; to: PrintEndpoint; critical: boolean }

type T = (key: string, opts?: any) => string

export const buildPrintRows = (
  rows: VisibleRow<ScheduleActivity>[],
  predecessorsByActivity: Map<string, { local: LocalLink[] }>,
  tree: Tree<ScheduleActivity>,
): PrintRow[] =>
  rows.map((r) => {
    const a = r.node.activity
    const p = predecessorsByActivity.get(a.id)
    return { id: a.id, rowNumber: r.node.rowNumber, depth: r.node.depth, activity: a, predecessorsText: p ? formatPredecessors(p.local, tree) : '' }
  })

/** The same strings `GanttRow` renders (spec §4.2); `health` is its label, not the icon. */
export const printCellText = (column: ColumnId, row: PrintRow, t: T, labels: { health: Record<ScheduleHealth, string> }): string => {
  const a = row.activity
  switch (column) {
    case 'rowNumber': return String(row.rowNumber)
    case 'wbs': return a.wbsCode
    case 'name': return a.name
    case 'owner': return a.owner ?? ''
    case 'duration': return formatDays(t, a.durationDays)
    case 'start': return formatIsoDate(t, a.start)
    case 'finish': return formatIsoDate(t, a.finish)
    case 'predecessors': return row.predecessorsText
    case 'percent': return String(a.isSummary ? a.rolledPercent : a.percentComplete)
    case 'float': return String(a.totalFloatDays)
    case 'health': return labels.health[a.health] ?? a.health
    default: return ''
  }
}

export interface PrintLinksInput {
  dependencies: ScheduleDependency[]
  byId: Map<string, ScheduleActivity>
  tree: Tree<ScheduleActivity>
  collapsedIds: Set<string>
  /** visible-row index by the id of the row that DISPLAYS an activity (its own row or its collapsed ancestor's) */
  rowIndexById: Map<string, number>
  includeCritical: boolean
}

/** Local links only (both endpoints in this schedule), endpoints relocated like `DependencyLayer` does (`visibleRowOf`). */
export const buildPrintLinks = ({ dependencies, byId, tree, collapsedIds, rowIndexById, includeCritical }: PrintLinksInput): PrintLink[] => {
  const out: PrintLink[] = []
  dependencies.forEach((d) => {
    const pred = byId.get(d.predecessorId)
    const succ = byId.get(d.successorId)
    if (!pred || !succ) return
    const fromDisplay = byId.get(visibleRowOf(tree, collapsedIds, pred.id))
    const toDisplay = byId.get(visibleRowOf(tree, collapsedIds, succ.id))
    if (!fromDisplay || !toDisplay) return
    const fromRow = rowIndexById.get(fromDisplay.id)
    const toRow = rowIndexById.get(toDisplay.id)
    if (fromRow == null || toRow == null) return
    out.push({
      id: d.id, type: d.type, fromRow, toRow,
      from: { start: fromDisplay.start, finish: fromDisplay.finish, isMilestone: fromDisplay.isMilestone },
      to: { start: toDisplay.start, finish: toDisplay.finish, isMilestone: toDisplay.isMilestone },
      critical: includeCritical && pred.isCritical && succ.isCritical,
    })
  })
  return out
}
